/**
 * Centralized helpers for turning backend `{ error, code, message }` responses
 * into copy that can be shown to the user.
 *
 * The backend returns an optional `code` (see backend/src/index.ts) — we map
 * that to a localized string via next-intl. When `code` is missing or unknown
 * we fall through to the backend's English `error`/`message`. That keeps the
 * pipeline incremental: backend endpoints can adopt codes one at a time, and
 * the frontend keeps working in the meantime.
 *
 * Each entry in `ERROR_CODE_TRANSLATORS` calls `t()` with a string literal so
 * next-intl's experimental extraction step picks it up at build time.
 */ import { isScenePointsLoadError } from '@/components/cameraMotion/scenePoints';
import type { ApiError, AuthAction } from './authenticatedRequest';
import { AuthCaptchaError } from './authCaptcha';
export type ApiErrorCode = 'TOKEN_EXPIRED' | 'MISSING_TOKEN' | 'NETWORK_ERROR' | 'EMAIL_ALREADY_EXISTS' | 'INSUFFICIENT_CREDITS' | 'PROJECT_SPEND_CAP_EXCEEDED' | 'MEMBER_CAP_EXCEEDED' | 'CREDIT_DEDUCTION_FAILED' | 'NO_BILLING_ACCOUNT' | 'PLAN_REQUIRED' | 'BYOK_PROVIDER_NOT_GRANTED' | 'BYOK_MISSING_KEY' | 'BYOK_SECRET_CHANGED' | 'BYOK_MISCONFIGURED' | 'MISSING_KEY' | 'INVALID_FAL_KEY' | 'INVALID_FAL_ADMIN_KEY' | 'FAL_KEY_REQUIRED' | 'FAL_ADMIN_KEY_REQUIRED' | 'FAL_ADMIN_KEY_WORKSPACE_MISMATCH' | 'INVALID_BILLING_MODE' | 'INVALID_DATE_RANGE';
type TFunction = (key: string, values?: Record<string, string | number>) => string;
/**
 * Each error code maps to a function that returns its localized string.
 *
 * The closure body has to call `t()` on a string literal so `pnpm i18n:extract`
 * can collect it. Building the string outside `t()` — for example via a
 * `Record<ApiErrorCode, string>` — would defeat extraction and leave Japanese
 * users with English fallbacks.
 */ const ERROR_CODE_TRANSLATORS: Record<ApiErrorCode, (t: TFunction) => string> = {
    TOKEN_EXPIRED: (t)=>t('Your session expired. Please refresh the page.'),
    MISSING_TOKEN: (t)=>t('You need to be signed in to do that.'),
    NETWORK_ERROR: (t)=>t('Network error. Check your connection and try again.'),
    EMAIL_ALREADY_EXISTS: (t)=>t('This email is already registered.'),
    INSUFFICIENT_CREDITS: (t)=>t("You don't have enough credits for this generation."),
    PROJECT_SPEND_CAP_EXCEEDED: (t)=>t('This project has hit its spend cap.'),
    MEMBER_CAP_EXCEEDED: (t)=>t('You have hit your team spend cap.'),
    CREDIT_DEDUCTION_FAILED: (t)=>t("We couldn't reserve credits for this generation. Try again."),
    NO_BILLING_ACCOUNT: (t)=>t('No billing account is configured for this team.'),
    PLAN_REQUIRED: (t)=>t('This feature requires a Pro or Team plan.'),
    BYOK_PROVIDER_NOT_GRANTED: (t)=>t('Bring-your-own-key is not available for this team.'),
    BYOK_MISSING_KEY: (t)=>t('Add a FAL key before enabling BYOK.'),
    BYOK_SECRET_CHANGED: (t)=>t('The FAL key changed while enabling BYOK. Try again.'),
    BYOK_MISCONFIGURED: (t)=>t('BYOK is misconfigured for this team. Contact the team owner.'),
    MISSING_KEY: (t)=>t('A required key is missing.'),
    INVALID_FAL_KEY: (t)=>t('FAL rejected this key.'),
    INVALID_FAL_ADMIN_KEY: (t)=>t('FAL rejected this admin key.'),
    FAL_KEY_REQUIRED: (t)=>t('Save a FAL generation key before adding an admin key.'),
    FAL_ADMIN_KEY_REQUIRED: (t)=>t('A FAL admin key is required.'),
    FAL_ADMIN_KEY_WORKSPACE_MISMATCH: (t)=>t('This admin key belongs to a different FAL workspace.'),
    INVALID_BILLING_MODE: (t)=>t('Billing mode must be "byok" or "martini".'),
    INVALID_DATE_RANGE: (t)=>t('Invalid date range.')
};
/**
 * Pull the most useful information out of any error-like value.
 */ function extractApiError(err: unknown): {
    code?: string;
    backendMessage?: string;
} {
    if (!err) return {};
    // Before the `code`/`message` sniff below, which these would otherwise
    // satisfy: the request plumbing's own errors carry machine-written text (a
    // failed token refresh reads "Authentication refresh failed: … Error code:
    // TOKEN_REFRESH_FAILED."). Returning nothing drops `formatApiError` to the
    // caller's fallback, which is the copy actually written for that screen.
    if (isPlumbingError(err)) return {};
    if (typeof err === 'object') {
        const maybeApi = err as Partial<ApiError> & {
            billingInfo?: {
                code?: string;
            };
        };
        const code = maybeApi.code ?? maybeApi.billingInfo?.code;
        const backendMessage = maybeApi.error ?? maybeApi.message;
        if (code || backendMessage) return {
            code,
            backendMessage
        };
    }
    if (err instanceof Error) {
        return {
            backendMessage: err.message
        };
    }
    if (typeof err === 'string') {
        return {
            backendMessage: err
        };
    }
    return {};
}
function isKnownErrorCode(code: string | undefined): code is ApiErrorCode {
    return !!code && code in ERROR_CODE_TRANSLATORS;
}
/**
 * Format an API error for user display.
 *
 * Lookup order:
 *   1. If the error carries a known `code`, return the localized string.
 *   2. If the backend sent an English `error`/`message`, return that string.
 *   3. Fall back to a generic "something went wrong" message.
 *
 * The caller passes its already-bound `useExtracted` `t` function.
 */ export function formatApiError(err: unknown, t: TFunction, options: {
    fallback?: string;
} = {}): string {
    const { code, backendMessage } = extractApiError(err);
    if (isKnownErrorCode(code)) {
        return ERROR_CODE_TRANSLATORS[code](t);
    }
    if (backendMessage) {
        // Backend strings are English-only today. We surface them verbatim so that
        // operators see the same diagnostic copy in either locale until the
        // backend adopts the corresponding `code`.
        return backendMessage;
    }
    if (options.fallback) return options.fallback;
    return t('Something went wrong. Please try again.');
}
// ---------------------------------------------------------------------------
// Real-account gating error handling (parseSafeApiError / RequiresRealAccountError).
// Independent of the localized formatApiError system above; kept from dev mainline.
// ---------------------------------------------------------------------------
export const REQUIRES_REAL_ACCOUNT_CODE = 'REQUIRES_REAL_ACCOUNT';
export interface SafeApiError {
    error: string;
    code?: string;
    message?: string;
    required?: number;
    available?: number;
    [key: string]: unknown;
}
export class RequiresRealAccountError extends Error {
    code = REQUIRES_REAL_ACCOUNT_CODE;
    constructor(message = 'This feature requires a real account. Please sign up to continue.'){
        super(message);
        this.name = 'RequiresRealAccountError';
    }
}
/**
 * The session is still believed to be good but we couldn't produce a usable token
 * for this request — normally because Supabase was briefly unreachable. Retryable:
 * the caller should back off, not sign the user out.
 *
 * Lives here rather than in authenticatedRequest so `sentry.ts` can identify it
 * without importing that module back (same arrangement as RequiresRealAccountError).
 */ export class AuthRefreshUnavailableError extends Error {
    code: string;
    authAction: AuthAction = 'none';
    constructor(message: string, code: string){
        super(message);
        this.name = 'AuthRefreshUnavailableError';
        this.code = code;
    }
}
export function isAuthRefreshUnavailableError(error: unknown): error is AuthRefreshUnavailableError {
    return error instanceof AuthRefreshUnavailableError;
}
/**
 * A 401 whose `authAction` the client does not recognize, or an auth error body
 * it could not parse. The message is assembled from backend diagnostics
 * ("Authentication error: … Error code: … Please check your credentials…") and
 * is meant for whoever is debugging, so it must not reach a user — see
 * `userFacingErrorMessage`.
 *
 * Was a plain `Error`, which `isPassthroughRequestError` recognized by a
 * `startsWith('Authentication')` message check. That string test stays where it
 * is; this class exists so the display layer can classify without it.
 */ export class AuthRequestError extends Error {
    constructor(message: string){
        super(message);
        this.name = 'AuthRequestError';
    }
}
export function isAuthRequestError(error: unknown): error is AuthRequestError {
    return error instanceof AuthRequestError;
}
/**
 * A queued/background request was bound to a different authenticated user.
 * Rejecting it is an expected safety outcome, not a request failure.
 *
 * This lives beside the other shared API errors so Sentry can classify it
 * without importing authenticatedRequest back into sentry.ts.
 */ export class AuthenticatedRequestUserMismatchError extends Error {
    constructor(expectedUserId: string){
        super(`Authenticated request user does not match queued owner ${expectedUserId}`);
        this.name = 'AuthenticatedRequestUserMismatchError';
    }
}
export function isAuthenticatedRequestUserMismatchError(error: unknown): error is AuthenticatedRequestUserMismatchError {
    return error instanceof AuthenticatedRequestUserMismatchError;
}
/**
 * The browser never got an HTTP response — DNS/TLS/CORS/offline/connection abort.
 * Distinct from a non-2xx status, which still produces a Response.
 *
 * Constructed in `authenticatedRequest` so Sentry attributes the failure to that
 * client (and the request path) instead of the PostHog fetch shim.
 */ export class ApiTransportError extends Error {
    code = 'TRANSPORT_ERROR';
    endpoint: string;
    requestPath: string;
    requestHost?: string;
    /** How many fetch attempts were made before giving up (1 = no retry). */ attempts: number;
    /**
   * Already reported at the transport choke point. Callers that catch this
   * error and hand it to `captureSentryError` (the library snapshot fetch does)
   * are dropped, so one incident stays one Sentry group instead of minting a
   * second one per catching module.
   */ reportedToSentry = false;
    constructor(message: string, options: {
        endpoint: string;
        requestPath: string;
        requestHost?: string;
        attempts?: number;
        cause?: unknown;
    }){
        super(message);
        this.name = 'ApiTransportError';
        this.endpoint = options.endpoint;
        this.requestPath = options.requestPath;
        this.requestHost = options.requestHost;
        this.attempts = options.attempts ?? 1;
        if (options.cause !== undefined) {
            this.cause = options.cause;
        }
    }
}
/**
 * `ApiTransportError.message`. Deliberately plain copy and not the diagnostic
 * string, because ~200 call sites render a caught error's `.message` straight
 * to the user; the host-bearing version is re-applied at the Sentry boundary
 * (see `apiTransportErrorMessage` and the `beforeSend` hook), so the issue
 * title is unchanged. `requestHost` / `endpoint` / `attempts` stay on the
 * instance for anyone debugging from a console or a log.
 */ export const API_TRANSPORT_USER_MESSAGE = 'Connection lost. Check your connection and try again.';
/**
 * The Sentry issue title for a transport failure. The host is part of the
 * message so the group is searchable and alertable by the endpoint it names
 * ("Failed to fetch (api.martini.film)") rather than by a bare browser string.
 *
 * Applied in `beforeSend` rather than at construction — see
 * `API_TRANSPORT_USER_MESSAGE` for why the two strings had to split.
 */ export function apiTransportErrorMessage(cause: unknown, requestHost: string | undefined): string {
    const base = cause instanceof Error && cause.message ? cause.message : 'Failed to fetch';
    if (!requestHost) return base;
    // Chrome 151 already names the host in its own TypeError ("Failed to fetch
    // (api.martini.film)"), so appending unconditionally produced the doubled
    // title seen in production. Safari ("Load failed") and Firefox still need it.
    return base.includes(`(${requestHost})`) ? base : `${base} (${requestHost})`;
}
/**
 * Group every transport failure against the same API host into ONE Sentry issue.
 *
 * Keyed on the host, deliberately NOT on the request path or the calling module:
 * a single connectivity blip fans out across every path a page loads, so a
 * per-path (or stack-derived) key minted a dozen "1 event / 1 user" issues per
 * incident and the family could not be counted, alerted on, or trended. The path
 * stays available as the `request_path` tag for drill-down.
 *
 * A request built from a relative URL has no parseable host. It falls back to a
 * constant rather than to the path, so grouping can never silently revert to the
 * per-path split this key exists to prevent (ENG-749).
 */ export const API_TRANSPORT_UNKNOWN_HOST = 'unknown-host';
export function apiTransportFingerprint(requestHost: string | undefined): string[] {
    return [
        'api-transport-error',
        requestHost || API_TRANSPORT_UNKNOWN_HOST
    ];
}
export function isApiTransportError(error: unknown): error is ApiTransportError {
    return error instanceof ApiTransportError;
}
/**
 * The backend answered 404 for the route itself, not for the thing being asked
 * about — i.e. this deploy of the frontend is running ahead of a backend that
 * doesn't serve the endpoint yet. Frontend and backend release independently
 * (Vercel vs Fly), so a newly added endpoint has a window where it 404s.
 *
 * Only meaningful for endpoints whose handler tags its own not-found responses
 * with a `code`; without that, a missing row and a missing route are the same
 * bare 404 and callers must not treat them differently.
 */ export class BackendRouteUnavailableError extends Error {
    code = 'BACKEND_ROUTE_UNAVAILABLE';
    endpoint: string;
    constructor(endpoint: string){
        super(`Backend route ${endpoint} is not available on this deployment`);
        this.name = 'BackendRouteUnavailableError';
        this.endpoint = endpoint;
    }
}
export function isBackendRouteUnavailableError(error: unknown): error is BackendRouteUnavailableError {
    return error instanceof BackendRouteUnavailableError;
}
export function isAbortError(error: unknown): boolean {
    return ((typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') || (error instanceof Error && error.name === 'AbortError'));
}
/**
 * Error text that is safe to render to a user.
 *
 * Most caught errors carry a server-authored message worth showing, so the
 * default is to pass `err.message` through. The exceptions are the request
 * plumbing's own errors, whose text is written for us: a dropped connection
 * reads "Failed to fetch (api.martini.film)", and a failed token refresh reads
 * "Authentication refresh failed: … Error code: TOKEN_REFRESH_FAILED.". Those
 * get the caller's copy instead.
 *
 * (`ByokKeySection` uses a stricter allow-list — it knows its full error surface
 * is `ByokKeyError` — but this deny-list is the right default elsewhere.)
 */ export function userFacingErrorMessage(err: unknown, fallback: string): string {
    if (isPlumbingError(err)) return fallback;
    return err instanceof Error && err.message ? err.message : fallback;
}
/**
 * Errors thrown by the request layer itself rather than by the backend. Their
 * `message` is written for whoever is debugging, so no display path should use
 * it. `ApiTransportError` is in the list even though its own message is now
 * user copy — a caller's localized fallback still beats the English constant.
 */ function isPlumbingError(err: unknown): boolean {
    return (err instanceof AuthCaptchaError || isApiTransportError(err) || isAuthRefreshUnavailableError(err) || isAuthenticatedRequestUserMismatchError(err) || isAuthRequestError(err) || isBackendRouteUnavailableError(err) || // The scene loader's messages carry byte counts, magic numbers and HTTP
    // statuses — operator copy that shipped to a first-run user before ENG-792.
    isScenePointsLoadError(err));
}
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Pathname only — no query/hash, so tokens in the URL never land on the Sentry tag.
 *
 * UUID segments collapse to `:id` because this doubles as the Sentry fingerprint:
 * a raw `/organizations/<uuid>/byok-key` mints a separate issue per org, which
 * splits one bug across dozens of issues and keeps any alert threshold from firing.
 */ export function apiRequestPath(url: string): string {
    return pathnameOf(url).split('/').map((segment)=>(UUID_SEGMENT.test(segment) ? ':id' : segment)).join('/');
}
function pathnameOf(url: string): string {
    try {
        return new URL(url, 'https://invalid.local').pathname;
    } catch  {
        return url.split(/[?#]/, 1)[0] || url;
    }
}
export function apiRequestHost(url: string): string | undefined {
    try {
        const host = new URL(url).host;
        return host || undefined;
    } catch  {
        return undefined;
    }
}
export function isRequiresRealAccountError(error: unknown): error is RequiresRealAccountError {
    if (error instanceof RequiresRealAccountError) return true;
    if (!error || typeof error !== 'object') return false;
    const candidate = error as {
        code?: unknown;
        errorCode?: unknown;
        message?: unknown;
    };
    if (candidate.code === REQUIRES_REAL_ACCOUNT_CODE || candidate.errorCode === REQUIRES_REAL_ACCOUNT_CODE) {
        return true;
    }
    return typeof candidate.message === 'string' && candidate.message.includes(REQUIRES_REAL_ACCOUNT_CODE);
}
export async function parseSafeApiError(response: Response): Promise<SafeApiError> {
    try {
        const parsed: unknown = await response.json();
        if (parsed && typeof parsed === 'object') {
            const body = parsed as Record<string, unknown>;
            return {
                ...body,
                error: typeof body.error === 'string' ? body.error : response.statusText || 'Request failed',
                code: typeof body.code === 'string' ? body.code : undefined,
                message: typeof body.message === 'string' ? body.message : undefined,
                required: typeof body.required === 'number' ? body.required : undefined,
                available: typeof body.available === 'number' ? body.available : undefined
            };
        }
    } catch  {
    // Fall through to the stable fallback shape below.
    }
    return {
        error: response.statusText || 'Request failed'
    };
}
